import React, { useEffect, useMemo } from 'react';
import { ModalFeedbackRequestViewProps } from './types';
import { Button } from '@vtbhome/juicyfront';
import { Footer, InfoGroup, FieldsGroup, Body } from './modal-feedback-request-view.styles';
import { Typography } from '@shared/ui/typography/typography';
import { NetworkStateNew } from '@shared/ui/network-state-new/network-state-new';
import { useFeedbackRequestDetailSharedQuery } from '@entities/feedback-request/libs/hooks/use-feedback-request-detail-shared-query/use-feedback-request-detail-shared-query';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import dayjs from 'dayjs';
import { EntityRelatedUser } from '@shared/ui/entity-related-user/entity-related-user';
import { Modal } from '@shared/ui/modal/modal';
import { Textarea } from '@shared/ui/textarea/textarea';
import { FeedbackRequestStatusBadge } from '@entities/feedback-request/ui/feedback-request-status-badge/feedback-request-status-badge';
import { Field, Form } from 'react-final-form';
import { useFeedbackRequestChangeStatusMutation } from '@entities/feedback-request/api/feedback-request-api';
import { CACHE_KEY_FEEDBACK_REQUEST_CHANGE_STATUS } from '@entities/feedback-request/config/api-constants';
import { FeedbackRequestStatus } from '@shared/model/feedback-request';
import { FEEDBACK_REQUEST_NEXT_STEP_STATUS_MAP } from '@entities/feedback-request/config/ui-constants';
import { validate } from '../../lib/form-validate';
import { FEEDBACK_REQUEST_ICON_STATUS_MAP, FEEDBACK_REQUEST_TEXT_STATUS_MAP } from '../../config/ui-constants';
import { EditFormKeys } from '../../config/form-constants';
import { MutateFormFields } from '../../model/types';
import { useResetModalForm } from '@shared/lib/hooks/use-reset-modal-form/use-reset-modal-form';

export const ModalFeedbackRequestView: React.FC<ModalFeedbackRequestViewProps> = ({
    isShow,
    feedbackRequestId,
    onClose,
}) => {
    const formRef = useResetModalForm<MutateFormFields>(isShow);
    const [getFeedbackRequestDetail, feedbackRequestDetailResult] = useFeedbackRequestDetailSharedQuery(
        feedbackRequestId || 0
    );
    const [changeFeedbackRequestStatus, feedbackRequestChangeStatusResult] = useFeedbackRequestChangeStatusMutation({
        fixedCacheKey: CACHE_KEY_FEEDBACK_REQUEST_CHANGE_STATUS,
    });

    const nextStatus = useMemo(
        () =>
            feedbackRequestDetailResult.data &&
            FEEDBACK_REQUEST_NEXT_STEP_STATUS_MAP[feedbackRequestDetailResult.data.status],
        [feedbackRequestDetailResult.data?.status]
    );

    const handleSubmit = (status?: FeedbackRequestStatus | null) => (fields: MutateFormFields) => {
        feedbackRequestDetailResult.data &&
            status &&
            changeFeedbackRequestStatus({
                id: feedbackRequestDetailResult.data.id,
                status,
                ...fields,
            });
    };

    const feedbackRequestDetail = feedbackRequestDetailResult.data;
    const error = getQueryError(feedbackRequestDetailResult);
    const isLoading =
        feedbackRequestDetailResult.isFetching ||
        feedbackRequestDetailResult.isLoading ||
        feedbackRequestDetailResult.isUninitialized;

    useEffect(() => {
        if (feedbackRequestId) {
            getFeedbackRequestDetail(feedbackRequestId);
        }
    }, [feedbackRequestId]);

    return (
        <Form
            onSubmit={handleSubmit(nextStatus)}
            validate={validate}
            render={(form) => {
                formRef.current = form;
                return (
                    <Modal
                        isShow={isShow}
                        variant='drawer'
                        size='l'
                        header={<Typography size='h6'>Обращение</Typography>}
                        footer={
                            <Footer>
                                <Button buttonType='light' size='l' onClick={onClose}>
                                    Отменить
                                </Button>
                                {feedbackRequestDetailResult.data && nextStatus && (
                                    <Button
                                        buttonType='primary'
                                        size='l'
                                        preloader={feedbackRequestChangeStatusResult.isLoading}
                                        startAdornment={
                                            FEEDBACK_REQUEST_ICON_STATUS_MAP[feedbackRequestDetailResult.data.status]
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            form.handleSubmit(e);
                                        }}>
                                        {FEEDBACK_REQUEST_TEXT_STATUS_MAP[feedbackRequestDetailResult.data.status]}
                                    </Button>
                                )}
                            </Footer>
                        }
                        onClose={onClose}>
                        <NetworkStateNew
                            size='s'
                            isLoading={isLoading}
                            isError={Boolean(error)}
                            isInitialized={!isLoading}
                            isFiltered={false}
                            hasData={Boolean(feedbackRequestDetail)}
                            errorProps={{
                                title: 'Ошибка загрузки обращения',
                                description: error?.message,
                            }}>
                            {feedbackRequestDetail && (
                                <Body>
                                    <InfoGroup>
                                        <Typography size='body1_short' color='neutral_50'>
                                            ID обращения
                                        </Typography>
                                        <Typography size='body1_long'>{`№${feedbackRequestDetail.id}`}</Typography>
                                    </InfoGroup>
                                    <FieldsGroup>
                                        <InfoGroup>
                                            <Typography size='body1_short' color='neutral_50'>
                                                Статус
                                            </Typography>
                                            <FeedbackRequestStatusBadge status={feedbackRequestDetail.status} />
                                        </InfoGroup>
                                        <InfoGroup>
                                            <Typography size='body1_short' color='neutral_50'>
                                                Дата создания
                                            </Typography>
                                            <Typography size='body1_long'>
                                                {feedbackRequestDetail.createdAt
                                                    ? dayjs(feedbackRequestDetail.createdAt).format('DD.MM.YY')
                                                    : '-'}
                                            </Typography>
                                        </InfoGroup>
                                    </FieldsGroup>
                                    <FieldsGroup>
                                        <InfoGroup>
                                            <Typography size='body1_short' color='neutral_50'>
                                                ID (сделки или продукта)
                                            </Typography>
                                            {feedbackRequestDetail.data?.id && (
                                                <Typography size='body1_long'>{`№${feedbackRequestDetail.data.id}`}</Typography>
                                            )}
                                        </InfoGroup>
                                        <InfoGroup>
                                            <Typography size='body1_short' color='neutral_50'>
                                                Дата обновления
                                            </Typography>
                                            <Typography size='body1_long'>
                                                {feedbackRequestDetail.updatedAt
                                                    ? dayjs(feedbackRequestDetail.updatedAt).format('DD.MM.YY')
                                                    : '-'}
                                            </Typography>
                                        </InfoGroup>
                                    </FieldsGroup>
                                    <InfoGroup>
                                        <Typography size='body1_short' color='neutral_50'>
                                            ФИО клиента, создавшего обращение
                                        </Typography>
                                        <EntityRelatedUser user={feedbackRequestDetail?.client} />
                                    </InfoGroup>
                                    <InfoGroup>
                                        <Typography size='body1_short' color='neutral_50'>
                                            ФИО администратора, работающего с обращением
                                        </Typography>
                                        <EntityRelatedUser user={feedbackRequestDetail?.admin} />
                                    </InfoGroup>
                                    <InfoGroup>
                                        <Typography size='body1_short' color='neutral_50'>
                                            Тема
                                        </Typography>
                                        <Typography size='body1_long'>{feedbackRequestDetail.subject.title}</Typography>
                                    </InfoGroup>
                                    <InfoGroup>
                                        <Typography size='body1_short' color='neutral_50'>
                                            Текст обращения
                                        </Typography>
                                        <Typography size='body1_long'>{feedbackRequestDetail.message}</Typography>
                                    </InfoGroup>

                                    {feedbackRequestDetail.status === 'progress' ? (
                                        <Field
                                            name={EditFormKeys.StatusComment}
                                            render={({ input, meta }) => (
                                                <Textarea
                                                    {...input}
                                                    label='Комментарий администратора'
                                                    placeholder='Введите комментарий'
                                                    maxLength={255}
                                                    error={meta.touched && meta.error}
                                                />
                                            )}
                                        />
                                    ) : (
                                        feedbackRequestDetail.statusComment && (
                                            <InfoGroup>
                                                <Typography size='body1_short' color='neutral_50'>
                                                    Комментарий администратора
                                                </Typography>
                                                <Typography size='body1_long'>
                                                    {feedbackRequestDetail.statusComment}
                                                </Typography>
                                            </InfoGroup>
                                        )
                                    )}
                                    <Typography size='h6'>Дополнительно</Typography>
                                    <FieldsGroup>
                                        <InfoGroup>
                                            <Typography size='body1_short' color='neutral_50'>
                                                Браузер клиента
                                            </Typography>
                                            <Typography size='body1_long'>{feedbackRequestDetail.userAgent}</Typography>
                                        </InfoGroup>
                                        <InfoGroup>
                                            <Typography size='body1_short' color='neutral_50'>
                                                IP клиента
                                            </Typography>
                                            <Typography size='body1_long'>{feedbackRequestDetail.ip}</Typography>
                                        </InfoGroup>
                                    </FieldsGroup>
                                    <InfoGroup>
                                        <Typography size='body1_short' color='neutral_50'>
                                            URL страницы
                                        </Typography>
                                        <Typography size='body1_long'>{feedbackRequestDetail.httpReferer}</Typography>
                                    </InfoGroup>
                                </Body>
                            )}
                        </NetworkStateNew>
                    </Modal>
                );
            }}
        />
    );
};
